/**
 * admin_subscriptions_controller.js — Admin > Subscriptions page (Stimulus).
 *
 * Loads and saves the free tier's monthly job limit and the Pro trial length,
 * filters the table's rows by Subscribed / Free / Demo, and runs a row's Reset
 * usage and Cancel subscription actions. A change toasts and re-renders the
 * page in place through window.chippiePageCheck.refresh().
 *
 * Each number field's defaultValue moves with its value whenever the server's
 * number is shown: page-check counts a field whose value differs from its
 * default as typing in progress and holds the refresh until it matches.
 *
 * Targets: limit and trialDays (the number inputs), filter (the three checkboxes, each with
 * data-kind subscribed|free|demo), count (the visible-rows badge). Rows are
 * the table's tr[data-tier] (data-demo "true" for a demo business).
 *
 * Template: templates/admin/subscription_management.html
 */

import { Controller } from "@hotwired/stimulus";

const LIMIT_URL = '/admin/api/subscription/free-tier-limit';
const TRIAL_DAYS_URL = '/admin/api/subscription/trial-days';

export default class extends Controller {
    static targets = ['limit', 'trialDays', 'filter', 'count'];

    connect() {
        this.filter();
        fetch(LIMIT_URL, { credentials: 'same-origin' })
            .then(function (response) { return response.json(); })
            .then((data) => { if (data.success) this._showLimit(data.limit); })
            .catch(function (error) { console.error('Free tier limit not loaded:', error); });
        fetch(TRIAL_DAYS_URL, { credentials: 'same-origin' })
            .then(function (response) { return response.json(); })
            .then((data) => { if (data.success) this._showTrialDays(data.days); })
            .catch(function (error) { console.error('Pro trial length not loaded:', error); });
    }

    filter() {
        const shown = {};
        this.filterTargets.forEach(function (box) { shown[box.dataset.kind] = box.checked; });
        let visible = 0;
        this.element.querySelectorAll('tbody tr[data-tier]').forEach(function (row) {
            const tierOk = row.dataset.tier === 'free' ? shown.free : shown.subscribed;
            const show = tierOk && (row.dataset.demo !== 'true' || shown.demo);
            row.style.display = show ? '' : 'none';
            if (show) visible++;
        });
        if (this.hasCountTarget) this.countTarget.textContent = visible;
    }

    saveLimit() {
        const limit = parseInt(this.limitTarget.value, 10);
        if (isNaN(limit) || limit < 0 || limit > 1000) {
            window.showNotification('Enter a limit between 0 and 1000', 'error');
            return;
        }
        this._send(LIMIT_URL, { limit: limit }, 'Free tier limit saved', () => this._showLimit(limit));
    }

    /** The server owns the allowed range and answers with the reason when a value is out of it. */
    saveTrialDays() {
        const days = parseInt(this.trialDaysTarget.value, 10);
        this._send(TRIAL_DAYS_URL, { days: days }, 'Pro trial length saved', () => this._showTrialDays(days));
    }

    resetUsage(event) {
        const { id, name } = event.params;
        if (!confirm(`Reset usage for "${name}"?\n\nThis will set jobs, quotes, and workflows created this month back to 0.`)) return;
        this._send(`/admin/api/subscription/reset-usage/${id}`, null, null);
    }

    cancel(event) {
        const { id, name, stripe } = event.params;
        const effect = stripe
            ? 'This will cancel the Stripe subscription at the end of the current billing period and downgrade to free tier.'
            : 'This will downgrade the business to free tier immediately.';
        if (!confirm(`Cancel subscription for "${name}"?\n\n${effect}`)) return;
        this._send(`/admin/api/subscription/cancel/${id}`, null, 'Subscription cancelled');
    }

    _showLimit(limit) {
        this.limitTarget.value = limit;
        this.limitTarget.defaultValue = limit;
    }

    _showTrialDays(days) {
        this.trialDaysTarget.value = days;
        this.trialDaysTarget.defaultValue = days;
    }

    /** POST, toast the answer (the server's message when it gives one), then refresh. */
    _send(url, body, success, onSaved) {
        const options = { method: 'POST', credentials: 'same-origin' };
        if (body) {
            options.headers = { 'Content-Type': 'application/json' };
            options.body = JSON.stringify(body);
        }
        fetch(url, options)
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (!data.success) throw new Error(data.message || data.error || 'Unknown error');
                if (onSaved) onSaved();
                window.showNotification(data.message || success, 'success');
                window.chippiePageCheck.refresh();
            })
            .catch(function (error) {
                window.showNotification('Failed: ' + error.message, 'error');
            });
    }
}
